'use client'

import { useEffect, useMemo, useSyncExternalStore } from 'react'
import {
  defaultLocale,
  localeDirection,
  locales,
  matchAcceptLanguage,
  type Locale,
} from '@/i18n/config'
import ar from '@/messages/ar.json'
import en from '@/messages/en.json'
import zh from '@/messages/zh.json'
import { RefreshCw } from 'lucide-react'
import posthog from 'posthog-js'

import { Button } from '@/components/ui/button'
import { createTraceId } from '@/lib/trace-context'
import {
  isChunkLoadError,
  reloadAfterChunkError,
} from '@/lib/chunk-load-recovery'

// global-error replaces the root layout entirely, so global styles must be
// re-imported here — they are not inherited from app/layout.tsx.
import './globals.css'

// The root fallback cannot rely on the provider from the failed layout.
function rootErrorLocale(): Locale {
  try {
    const cookie = document.cookie.split(';').map(value => value.trim())
      .find(value => value.startsWith('NEXT_LOCALE='))?.slice('NEXT_LOCALE='.length)
    if (locales.includes(cookie as Locale)) return cookie as Locale
    return matchAcceptLanguage(navigator.languages.join(',')) ?? defaultLocale
  } catch {
    return defaultLocale
  }
}
const subscribeLocale = () => () => {}
const serverLocale = () => defaultLocale

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const locale = useSyncExternalStore(subscribeLocale, rootErrorLocale, serverLocale)
  const copy = { en, zh, ar }[locale].Errors
  const traceId = useMemo(() => createTraceId(), [])
  const isChunk = isChunkLoadError(error)

  useEffect(() => {
    const properties = {
      name: error.name,
      message: error.message,
      stack: error.stack?.slice(0, 200),
      digest: error.digest,
      trace_id: traceId,
    }

    if (reloadAfterChunkError(error)) {
      console.warn('[global-error] chunk load failure, reloading', properties)
      return
    }

    if (isChunk) return

    console.error('[global-error] application error', properties)
    try {
      posthog.capture('global_error', properties)
    } catch {
      // Avoid throwing while rendering the root error fallback.
    }
  }, [
    error,
    isChunk,
    error.name,
    error.message,
    error.stack,
    error.digest,
    traceId,
  ])

  return (
    <html lang={locale} dir={localeDirection(locale)}>
      <body className="font-sans antialiased">
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="border-border bg-card w-full max-w-md rounded-lg border p-8 shadow-sm">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="bg-muted flex size-12 items-center justify-center rounded-full">
                <RefreshCw className="text-muted-foreground size-6" />
              </div>
              <div>
                <h1 className="text-lg font-semibold">
                  {isChunk ? copy.newVersionAvailable : copy.applicationError}
                </h1>
                <p className="text-muted-foreground mt-1 text-sm">
                  {isChunk
                    ? copy.newVersionMessage
                    : copy.genericMessage}
                </p>
                {!isChunk ? (
                  <div className="border-border bg-muted/40 mt-4 rounded-md border px-3 py-2 text-start">
                    <p className="text-muted-foreground text-xs font-medium">
                      {copy.supportTraceId}
                    </p>
                    <p className="text-foreground mt-1 font-mono text-xs break-all">
                      {traceId}
                    </p>
                  </div>
                ) : null}
              </div>
              <div className="mt-2 flex gap-2">
                <Button onClick={() => window.location.reload()}>
                  <RefreshCw className="me-2 size-4" />
                  {copy.refreshPage}
                </Button>
                {!isChunk ? (
                  <Button variant="outline" onClick={reset}>
                    {copy.tryAgain}
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </body>
    </html>
  )
}
